'use client';

import Heading from '@/components/atoms/Heading';
import Text from '@/components/atoms/Text';
import Accordion from '@/components/atoms/Accordion';
import AccordionItem from '@/components/molecules/AccordionItem';
import Button from '@/components/atoms/Button';
import * as S from './styles';
import Breadcrumbs from '@/components/molecules/Breadcrumbs';
import { useRouter } from 'next/navigation';
import PlanoPaginaFallback from './fallback';
import formatarMoeda from '@/utils/formatarMoeda';
import { DataList } from '@/components/atoms/DataList';
import DataListItem from '@/components/molecules/DataListItem';
import formatarHTML from '@/utils/formatarHTML';
import { PlanoPageProps } from '../..';
import usePlanoPageHook from '../../hook/usePlanoPageHook';
import Banner from '@/components/atoms/Banner';
import { useTheme } from 'styled-components';
import { Fragment, useEffect } from 'react';
import { Separator } from '@/components/atoms/Separator';
import { AccordionsColumn } from '@/components/atoms/AccordionsList';
import ProvidersPlano from '@/components/organisms/ProvidersPlano';
import { useSacola } from '@/hooks/useSacola';
import { breakpointDesktopDefault } from '@/hooks/useBreakpoint';
import useMetodosUteis, {
  HTMLHigienizadoTag,
} from '@/hooks/useMetodosUteisHook';

export default function PlanoPageDesktop(props: PlanoPageProps) {
  const router = useRouter();
  const theme = useTheme();
  const { $comboSlug } = props;

  const { planoASerEditado, informarPlanoASerEditado, itens } = useSacola();

  const {
    combo,
    caminhoURL,
    produtoSelecionado,
    setProdutoSelecionado,
    atualizarProdutoPlanoSacola,
    // adicionarPlanoASacola,
    visualizarTermosCombo,
    comprarAgora,
  } = usePlanoPageHook($comboSlug, router);

  useEffect(() => {
    if (planoASerEditado === combo?.comboID) {
      setProdutoSelecionado(
        itens.find(
          (itemSacola) =>
            !!itemSacola.plano && itemSacola.plano.comboID === planoASerEditado,
        )?.opcaoSelecionadaPlano?.id,
      );
    }

    return () => {
      if (planoASerEditado === combo?.comboID) informarPlanoASerEditado(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoASerEditado, combo]);

  const { higienizarHTML } = useMetodosUteis();

  if (!combo) return <PlanoPaginaFallback />;

  return (
    <S.Container style={{ ...breakpointDesktopDefault, minHeight: 0 }}>
      <S.ContainerHeader>
        <Breadcrumbs
          links={[{ atual: true, href: caminhoURL, texto: 'Plano' }]}
        />
        <S.PlanoInfoTextos>
          <Heading
            $priority={1}
            $size={6}
            $color="neutral"
            $weight="bold"
            $align="left"
            $highContrast={true}
            $text={combo.nome ?? combo.descricao}
          />
        </S.PlanoInfoTextos>
      </S.ContainerHeader>
      <S.SideContainers>
        <S.ContainerPlanoInfo>
          <S.PlanoImagem>
            <Banner
              descricaoImagem={combo?.nome ?? combo?.descricao}
              priority={true}
              imagemUrl={combo.imagemMobile ?? combo.imagem}
              style={{
                borderRadius: theme.spacing[4],
              }}
            />
          </S.PlanoImagem>
          <S.PlanoInfoTextos>
            <Heading
              $priority={2}
              $size={4}
              $color="neutral"
              $weight="bold"
              $align="left"
              $highContrast={true}
              $text="Descrição"
            />
            <Text
              $size={2}
              $color="neutral"
              $weight="medium"
              $align="left"
              $highContrast={false}
              $text={combo?.descricao ?? ''}
            />
          </S.PlanoInfoTextos>
          <S.ContainerAccordion>
            <Accordion $size={1} $style="solid">
              <AccordionItem
                $size={4}
                $variant="ghost"
                $disabled="off"
                $title="Como utilizar"
                $conteudo={
                  <Fragment>
                    {combo.produtos.length ? (
                      <AccordionsColumn $espacamentoEntreAccordions={4}>
                        {combo.produtos
                          .filter(
                            (produto) =>
                              !produto.variavel ||
                              produto.id === produtoSelecionado,
                          )
                          .map((produto, index) => (
                            <Fragment key={produto.id}>
                              <AccordionItem
                                $size={1}
                                $variant="ghost"
                                $disabled="off"
                                $title={produto.produto}
                                $conteudo={
                                  <S.ContainerDetalhesAccordion>
                                    <HTMLHigienizadoTag
                                      html={higienizarHTML(
                                        formatarHTML(
                                          produto.comoUtilizar ?? '',
                                        ),
                                      )}
                                    />
                                  </S.ContainerDetalhesAccordion>
                                }
                              />
                              {index <
                                combo.produtos.filter(
                                  (produto) =>
                                    !produto.variavel ||
                                    produto.id === produtoSelecionado,
                                ).length -
                                  1 && (
                                <Separator
                                  $block={false}
                                  $orientation="horizontal"
                                />
                              )}
                            </Fragment>
                          ))}
                      </AccordionsColumn>
                    ) : (
                      <HTMLHigienizadoTag
                        html={higienizarHTML(
                          formatarHTML(combo.comoUtilizar ?? ''),
                        )}
                      />
                    )}
                  </Fragment>
                }
              />
            </Accordion>
          </S.ContainerAccordion>
        </S.ContainerPlanoInfo>
        <S.ContainerAcoesPlanos>
          {combo.produtos.length > 0 ? (
            <ProvidersPlano
              $tituloProvidersInclusos="Vem com:"
              $tituloProvidersInclusosTamanho={2}
              $tituloEscolhaProvidersVariaveis="Escolha o último"
              $tituloEscolhaProvidersVariaveisTamanho={4}
              $produtos={combo.produtos}
              $produtoSelecionadoID={produtoSelecionado}
              $selecionarProdutoSelecionado={setProdutoSelecionado}
              $alturaImagemProvidersInclusos={64}
              $larguraImagemProvidersInclusos={98}
              $alturaImagemProvidersVariaveis={40}
              $larguraImagemProvidersVariaveis={40}
            />
          ) : null}
          {combo?.beneficios && combo?.beneficios.length > 0 ? (
            <S.ContainerBeneficios>
              <Heading
                $priority={4}
                $size={4}
                $color="neutral"
                $weight="bold"
                $align="left"
                $highContrast={true}
                $text={
                  combo.produtos.length > 0 ? 'E ganhe também' : 'Benefícios'
                }
              />
              <DataList $size={2}>
                {combo.beneficios.map((beneficio) => (
                  <DataListItem
                    key={beneficio.id}
                    $size={2}
                    $label={beneficio.produto}
                    $conteudo={beneficio.nome}
                    $highContrast={false}
                    $orientation="horizontal"
                  />
                ))}
              </DataList>
            </S.ContainerBeneficios>
          ) : null}
          <S.ContainerPrecos>
            <Heading
              $priority={3}
              $size={8}
              $color="neutral"
              $weight="bold"
              $align="left"
              $highContrast={true}
              $text={formatarMoeda({
                valor: combo.preco,
              })}
            />
            {/* <Text
              $size={2}
              $color="neutral"
              $weight="medium"
              $align="left"
              $highContrast={false}
              $text="À vista no Pix ou no cartão"
            /> */}
          </S.ContainerPrecos>
          <S.ContainerBotoesPlano>
            <S.ContainerBotoesCompra>
              {/* {planoASerEditado !== combo?.comboID ? (
                <Button
                  $size={2}
                  $variant="soft"
                  $color="accent"
                  $highContrast={false}
                  $loading={false}
                  $focusRing={false}
                  $text="Adicionar a sacola"
                  $onClick={() => {
                    adicionarPlanoASacola();
                  }}
                  style={{ justifyContent: 'center', flex: 1 }}
                />
              ) : null} */}
              <Button
                $size={2}
                $variant="solid"
                $color="accent"
                $highContrast={false}
                $loading={false}
                $focusRing={false}
                $text={
                  planoASerEditado === combo?.comboID
                    ? 'Atualizar'
                    : 'Comprar agora'
                }
                $onClick={() => {
                  if (planoASerEditado === combo?.comboID) {
                    atualizarProdutoPlanoSacola();
                  } else {
                    comprarAgora();
                  }
                }}
                style={{ justifyContent: 'center', flex: 0.5 }}
              />
            </S.ContainerBotoesCompra>
            {combo.termosDeUso && planoASerEditado !== combo?.comboID ? (
              <S.ContainerBotoesCompra>
                <Button
                  $onClick={visualizarTermosCombo}
                  $size={2}
                  $variant="ghost"
                  $color="neutral"
                  $highContrast={true}
                  $loading={false}
                  $focusRing={false}
                  $text="Termos de uso"
                  style={{
                    justifyContent: 'center',
                    flex: 0.5,
                  }}
                />
              </S.ContainerBotoesCompra>
            ) : null}
          </S.ContainerBotoesPlano>
        </S.ContainerAcoesPlanos>
      </S.SideContainers>
    </S.Container>
  );
}
